/**
 * Women's / Men's switch for a look. Look product blocks carry
 * data-audience="women|men|all"; the ones not for the chosen side are hidden.
 */
class LookAudienceComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.#select(this.#initial());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  #initial() {
    const param = new URLSearchParams(window.location.search).get('look');
    if (param === 'women' || param === 'men') return param;

    const fromReferrer = this.#audienceFromPath(document.referrer);
    if (fromReferrer) return fromReferrer;

    return this.dataset.default === 'men' ? 'men' : 'women';
  }

  /** @param {string} url */
  #audienceFromPath(url) {
    if (!url) return null;
    let path = '';
    try {
      const parsed = new URL(url);
      if (parsed.origin !== window.location.origin) return null;
      path = parsed.pathname.toLowerCase();
    } catch (_) {
      return null;
    }
    // Whole words only, so "womens" is never read as "mens" and words that
    // merely contain the letters, such as "recommendations", are ignored.
    if (/(^|[\/\-_])womens?($|[\/\-_])/.test(path)) return 'women';
    if (/(^|[\/\-_])mens?($|[\/\-_])/.test(path)) return 'men';
    return null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const option = /** @type {HTMLElement} */ (event.target).closest('[data-audience]');
    if (option instanceof HTMLElement && option.dataset.audience) this.#select(option.dataset.audience);
  };

  /** Arrow keys move between the two options, as a radio group does */
  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    const options = [...this.querySelectorAll('[data-audience]')];
    const current = options.findIndex((el) => el.getAttribute('aria-checked') === 'true');
    const next = /** @type {HTMLElement} */ (options[(current + 1) % options.length]);
    event.preventDefault();
    if (next.dataset.audience) this.#select(next.dataset.audience);
    next.focus();
  };

  /** @param {string} audience */
  #select(audience) {
    this.querySelectorAll('[data-audience]').forEach((node) => {
      const option = /** @type {HTMLElement} */ (node);
      const checked = option.dataset.audience === audience;
      option.setAttribute('aria-checked', String(checked));
      option.tabIndex = checked ? 0 : -1;
    });

    // The whole look section: its images sit outside the details column
    const scope = this.closest('.product-information, .section, main') || document;
    scope.querySelectorAll('look-product-component[data-audience], .look-media__item[data-audience]').forEach((node) => {
      const look = /** @type {HTMLElement} */ (node);
      const show = look.dataset.audience === 'all' || look.dataset.audience === audience;
      look.toggleAttribute('data-audience-hidden', !show);
    });

    // The large first image in the gallery is the first one still showing
    scope.querySelectorAll('.look-media').forEach((gallery) => {
      const items = [...gallery.querySelectorAll('.look-media__item')];
      items.forEach((item) => item.removeAttribute('data-look-first'));
      items.find((item) => !item.hasAttribute('data-audience-hidden'))?.setAttribute('data-look-first', '');
    });


  }
}

if (!customElements.get('look-audience-component')) {
  customElements.define('look-audience-component', LookAudienceComponent);
}
